import { useEffect, useCallback, useState } from '@wordpress/element';
import moment from 'moment-timezone';
import counterTimer from '../../../resources/scripts/module/counter';
import {
  parseHubSpotShortcode,
  calculateSecondsToCount,
  getTimezoneDate,
} from '../utils/methods';
import $ from 'jquery';
import { getCurrentPostAcfFields } from '../utils/methods';

const { useSelect } = wp.data;
const { registerBlockType } = wp.blocks;
const {
  RichText,
  InspectorControls,
  InnerBlocks,
  MediaUpload,
  MediaUploadCheck,
  ColorPalette,
} = wp.blockEditor;
const { Button, PanelBody } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

registerBlockType('pg/event-content', {
  title: 'PG Event Content Block',
  icon: 'calendar',
  category: 'layout',
  attributes: {
    title: { type: 'string' },
    excerpt: { type: 'string' },
    eventDate: { type: 'string' },
    eventAddress: { type: 'string' },
    eventTransporation: { type: 'string' },
    eventUpcoming: { type: 'boolean', default: false },
    eventFormCode: { type: 'string', default: '' },
    customTag: { type: 'string', default: '' },
    titleRegister: { type: 'string' },
    eventImage: { type: 'string', default: '' },
    eventImageAlt: { type: 'string', default: '' },
    titleColor: { type: 'string', default: '' },
  },
  edit: ({ attributes, setAttributes }) => {
    const {
      title,
      excerpt,
      eventDate,
      eventUpcoming,
      eventFormCode,
      customTag,
      titleRegister,
      eventImage,
      eventImageAlt,
      titleColor,
    } = attributes;

    const [acfFields, setAcfFields] = useState([]);

    const metaData = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('meta'),
      []
    );

    const template = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('template'),
      []
    );

    useEffect(() => {
      setAcfFields(getCurrentPostAcfFields());
    }, []);

    const [loaded, setLoaded] = useState(false);
    const [changed, setChanged] = useState(false);

    const setAttributesData = useCallback(() => {
      if (metaData.date_of_event) {
        const eventDate = moment(metaData.date_of_event, 'YYYYMMDD').toDate();
        if (!isNaN(eventDate)) {
          setAttributes({ eventDate: eventDate.toISOString() });
          setAttributes({ eventUpcoming: eventDate > new Date() });
        }
      }

      setAttributes({
        eventFormCode:
          metaData.form_shortcode_of_event ||
          metaData.article_form_shortcode ||
          null,
      });

      if (metaData.address_of_event) {
        setAttributes({ eventAddress: metaData.address_of_event });
      }

      if (metaData.transporation_of_event) {
        setAttributes({ eventTransporation: metaData.transporation_of_event });
      }

      setChanged(!changed);
    }, [metaData, setAttributes]);

    const refreshMeta = useCallback(
      (event, field) => {
        event.preventDefault();
        event.stopPropagation();

        if (!metaData) {
          return;
        }

        if (field.data.type === 'date_picker') {
          metaData[field.data.name] = moment(
            event.target.value,
            'DD/MM/YYYY'
          ).format('YYYYMMDD');
        } else {
          metaData[field.data.name] = event.target.value;
        }

        setAttributesData();
      },
      [metaData, setAttributesData]
    );

    useEffect(() => {
      if (!metaData || !acfFields.length) {
        return;
      }

      setAttributesData();

      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
      );
      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).on('change', (event) =>
          refreshMeta(event, field)
        )
      );
    }, [metaData, acfFields, setAttributesData, template]);

    useEffect(() => {
      // Function to refresh all ACF fields
      const refreshAllACFFields = () => {
        // Check if metaData is available and valid
        if (!metaData) {
          return;
        }

        acfFields.forEach((field) =>
          $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
        );
      };

      refreshAllACFFields();
    }, [attributes, metaData, template]);

    useEffect(() => {
      if (loaded && eventFormCode && window.hbspt) {
        const { portalId, formId } = parseHubSpotShortcode(eventFormCode);

        if (portalId && formId) {
          const formClassTarget = `.my-hubspot-form-${formId}`; // Unique ID based on formId
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: formClassTarget,
          });
        }
      }
    }, [changed, loaded, eventFormCode, eventUpcoming]);

    useEffect(() => {
      if (eventUpcoming) {
        $('.counter, .event-counter').data(
          'seconds-to-count',
          attributes.eventDate
            ? getTimezoneDate(attributes.eventDate).unix()
            : 0
        );
        counterTimer(true); // Reset and start the counter
      }
    }, [eventDate, eventUpcoming]);

    const loadHubSpotScript = () => {
      if (window.hbspt) {
        setLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = '//js.hsforms.net/forms/v2.js';
      script.onload = () => setLoaded(true);
      document.body.appendChild(script);
    };

    useEffect(() => {
      loadHubSpotScript();
    }, []);

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Top Color Settings')}>
            <ColorPalette
              value={titleColor}
              onChange={(color) => setAttributes({ titleColor: color })}
            />
          </PanelBody>
        </InspectorControls>

        <div className="editor-component">
          <section className="section-light-primary">
            <article
              className={`post ${
                eventUpcoming ? 'post-event-upcoming' : 'post-event'
              }`}
            >
              <div className={'left'}>
                <header>
                  <div className="meta">
                    <RichText
                      tagName="span"
                      className="article-tag"
                      value={customTag}
                      placeholder={__('Enter custom tag...')}
                      onChange={(value) => setAttributes({ customTag: value })}
                      style={titleColor ? { color: titleColor } : {}}
                    />
                  </div>

                  <RichText
                    tagName="h2"
                    value={title}
                    className={'title'}
                    placeholder={__('Title...')}
                    onChange={(value) => setAttributes({ title: value })}
                    style={titleColor ? { color: titleColor } : {}}
                  />
                  <RichText
                    tagName="div"
                    className={'excerpt'}
                    placeholder={__('Post Excerpt..')}
                    value={excerpt}
                    onChange={(value) => setAttributes({ excerpt: value })}
                    style={titleColor ? { color: titleColor } : {}}
                  />
                  <div
                    className="event-counter"
                    data-timezone={themeSettings.timezone}
                    data-timestamp={
                      eventDate ? moment(eventDate).unix() - moment().unix() : 0
                    }
                  ></div>

                  {eventImage && (
                    <img
                      src={eventImage}
                      alt={eventImageAlt ?? ''}
                      className="background-image"
                    />
                  )}
                  <MediaUploadCheck>
                    <MediaUpload
                      onSelect={(media) =>
                        setAttributes({
                          eventImage: media.url,
                          eventImageAlt: media.alt,
                        })
                      }
                      allowedTypes={['image']}
                      value={eventImage}
                      render={({ open }) => (
                        <>
                          <Button
                            isPrimary
                            onClick={open}
                            className={'centeredFullButton'}
                          >
                            {eventImage
                              ? __('Change Image')
                              : __('Upload Image')}
                          </Button>
                        </>
                      )}
                    />
                  </MediaUploadCheck>
                </header>

                <div className="content">
                  <InnerBlocks
                    allowedBlocks={[
                      'core/paragraph',
                      'core/image',
                      'core/heading',
                      'core/list',
                      'core/embed',
                      'core/table',
                      'core/freeform',
                      'core/quote',
                      'pg/quote',
                      'pg/event-inner-details',
                      'pg/partners-block',
                      'pg/columns-section-inner',
                      'pg/button',
                      'pg/people',
                      'pg/banner',
                      'pg/list',
                    ]}
                  />
                </div>
              </div>

              <div className="right">
                <div className="green-box">
                  <RichText
                    tagName="h4"
                    value={titleRegister}
                    placeholder={__('Register to watch this event')}
                    onChange={(value) =>
                      setAttributes({ titleRegister: value })
                    }
                  />
                  <div
                    className={`my-hubspot-form-${
                      parseHubSpotShortcode(eventFormCode).formId || ''
                    }`}
                  ></div>
                </div>
              </div>
            </article>
          </section>
        </div>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    const {
      title,
      excerpt,
      eventDate,
      eventUpcoming,
      customTag,
      titleRegister,
      eventImage,
      eventImageAlt,
      eventFormCode,
      titleColor,
    } = attributes;

    return (
      <section className="section-light-primary">
        <article
          className={`post ${
            eventUpcoming ? 'post-event-upcoming' : 'post-event'
          }`}
        >
          <div className="left">
            <header>
              {customTag && (
                <div className="meta">
                  <span
                    className="article-tag"
                    style={titleColor ? { color: titleColor } : {}}
                  >
                    {customTag}
                  </span>
                </div>
              )}
              <h1
                className="title"
                style={titleColor ? { color: titleColor } : {}}
              >
                {title}
              </h1>
              <div
                className="excerpt"
                style={titleColor ? { color: titleColor } : {}}
              >
                {excerpt}
              </div>
              <div className="event-counter"></div>
              {eventImage && (
                <img
                  src={eventImage}
                  alt={eventImageAlt ?? ''}
                  className="background-image"
                />
              )}
            </header>
            <div className="content">
              <InnerBlocks.Content />
            </div>
          </div>
          {!eventUpcoming && (
            <div className="right">
              <div className="green-box">
                <h4>{titleRegister}</h4>
                <div
                  className={`my-hubspot-form-${
                    parseHubSpotShortcode(eventFormCode).formId || ''
                  }`}
                ></div>
              </div>
            </div>
          )}
        </article>
      </section>
    );
  },
});
